// kfe-self-record.js — Self-Recording exercise (grid + practice).
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/SR/SelfRecordExerciseScreen.cs
// iOS REFERENCE (Rule #9 — mirror exactly):
//   KFE-4.0-App/iEnglish/Classes/KFEModules/Learning/SelfRecording/
//     SelfRecordingContainerViewController.m  — thin entry wrapper (pushes the VC)
//     KFESRListPageVC.{h,m}                   — the WORD GRID  → <kfe-self-record-grid>
//     KFESRListCell.{h,m,xib}                 — a single tile
//     KFESelfRecordingVC.{h,m,xib}            — the PER-WORD PRACTICE screen (this file)
//     KFESelfRecordingViewModel.{h,m}         — state (playsSoFarCount/mustPlayCount, isNewReocrd)
//     KFESRDBManager.m                        — Mode dispatch (manual / automatic)
//   KFE-4.0-App/iEnglish/Classes/CustomViews/KFERecordingVoiceControl/
//     KFERecordingSoundControlView.{h,m}      — the 5-button bar → <kfe-recording-control-bar>
//
// TWO-SCREEN EXPERIENCE (iOS canon). iOS uses a UINavigationController push
// (KFESRListPageVC → KFESelfRecordingVC) plus a menuListBtn that pops back to
// the list. The web mirrors this as ONE mounted component with two internal
// views (`_view = "grid" | "practice"`), so the surrounding
// <kfe-exercise-host> wiring (active-time, item_completed → completion
// persistence + rollup invalidation + meter dispatch) stays intact across the
// grid↔practice switch — separate routes would unmount the host and lose it.
//   • Entry → GRID (the word tiles). Tap a tile → PRACTICE at that word.
//   • Practice has a "‹ Words" button (menuListBtn) → back to GRID.
//
// PER-WORD COMPLETION (iOS canon — KFESelfRecordingViewModel.m:349-358).
// `playsSoFarCount` increments when the learner plays back THEIR OWN recording
// after a fresh capture (`isNewReocrd`), NOT merely on record. A word is
// complete when `playsSoFarCount >= mustPlayCount` (default 1 → the iOS "0/1"
// tile counter). The ⏭ next button is gated on this (VC.m:175-181). When EVERY
// word is complete the item completes — we emit `item_completed`, which
// <kfe-exercise-host> persists via markItemCompleted → bumpProgressTick, so the
// pathway-list row's Brief 163 roll-up flips to ✓ exactly as it does for media
// items (Brief 171 success criterion #5).
//
// PERSISTENCE (Brief 171 → 172). Per-word `playsSoFarCount` is tracked in the
// `_progress` map and now persists per-word to the learner store's `srProgress`
// item-scoped store (Brief 172) — hydrated on mount (`_hydrateProgress`) and
// written fire-and-forget on each increment (`_recordWordPlay`), mirroring iOS
// updateDisplayingItemToLocal. So a partial session (e.g. 3 of 14 words)
// survives a reload. The ITEM-granularity item_completed funnel is unchanged —
// the per-word store is supplementary (Brief 172 success-criterion #3/#6).
//
// Config comes from /content/sr-config.json (Brief 060) keyed by itemId.
// Manual items list MMUs + per-entry displayType; Automatic items pull
// met-MMU ids and apply the item's defaultDisplayType (see sr-engine.js).
//
// **Recordings are local + transient + overwrite-per-session** by design
// (KFE-ONLINE-SHIPPING §0). No upload, no persistence, no per-recording event.
//
// DEFERRED (Brief 171 out of scope, flagged): KFESRCompletedVC — the
// all-words-done celebration screen. We currently reuse the Brief 049
// completion screen (Yes/No redo). KFEVoiceRecordAnimationView — iOS recording
// animation; the web uses a level meter (Rule #9 web-native substitute).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { resolveImage } from "../data/media-paths.js";
import { resolveConcepts as engineResolveConcepts } from "../engines/sr-engine.js";
import { Recorder } from "../core/recorder.js";

const MAX_RECORD_MS = 10_000;

/** @typedef {import("../engines/sr-engine.js").Concept} Concept */
/** @typedef {{ playSoFar: number, mustPlay: number, completed: boolean }} WordProgress */

export class KFESelfRecord extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 540px; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
    h2 { margin-top: 0; }

    .toolbar { display: flex; align-items: center; gap: 0.5rem; margin: 0.25rem 0 0.75rem; }
    .toolbar button.words { background: transparent; border: 0; color: var(--kfe-accent);
                            font-size: 0.95rem; cursor: pointer; padding: 0.25rem 0; }

    /* PRACTICE content body (contentImageView / contentLabel / contentTextLabel). */
    .stimulus { margin: 0.75rem 0; padding: 0.75rem; border-radius: 8px;
                background: var(--kfe-surface);
                display: flex; flex-direction: column; align-items: center;
                gap: 0.75rem; position: relative; min-height: 8rem; justify-content: center; }
    /* L2B .png line drawings are transparent — keep the canonical white image
       cell so they read against the dark theme (Brief 061). */
    .l2b-image-container { background: var(--kfe-l2b-image-bg);
                           border-radius: var(--kfe-l2b-image-radius);
                           padding: var(--kfe-l2b-image-padding);
                           box-sizing: border-box;
                           display: flex; align-items: center; justify-content: center; }
    .l2b-image-container.no-image { color: var(--kfe-bg); min-height: 3rem;
                                    padding: 0.75rem; font-size: 1rem;
                                    text-align: center; }
    .stimulus img { max-width: 100%; max-height: 240px; object-fit: contain;
                    border-radius: 8px; }
    /* Word name — large white text (iOS contentTextLabel / contentLabel). */
    .stimulus .word { font-size: 1.75rem; font-weight: 700; text-align: center;
                      color: var(--kfe-fg); }
    .stimulus .audio-only-label { color: var(--kfe-muted); font-style: italic;
                                  font-size: 1.5rem; }

    /* Info button (ⓘ) bottom-right of the content area; toggles the zh label. */
    .info-btn { position: absolute; bottom: 6px; right: 6px;
                width: 28px; height: 28px; border-radius: 999px;
                border: 1px solid var(--kfe-border); background: var(--kfe-bg);
                color: var(--kfe-fg); cursor: pointer; font-size: 0.95rem; line-height: 1; }
    .info-btn[aria-pressed="true"] { background: var(--kfe-accent); color: var(--kfe-on-accent); border: 0; }
    .info-btn[disabled] { opacity: 0.4; cursor: default; }
    .info-label { text-align: center; color: var(--kfe-muted); margin: 0.25rem 0 0; font-size: 1rem; }

    /* Recording level meter (web substitute for KFEVoiceRecordAnimationView). */
    .level { display: block; height: 0.75rem; margin: 0.5rem 0;
             background: var(--kfe-surface); border-radius: 999px;
             overflow: hidden; position: relative; }
    .level-fill { position: absolute; inset: 0 100% 0 0;
                  background: var(--kfe-accent); transition: right 60ms linear; }
    .recording-label { text-align: center; color: #d33; font-weight: 600; margin: 0 0 0.5rem; }

    .error { padding: 0.75rem 1rem; border-radius: 8px;
             background: #3d1f1f; color: #f5d8d8; margin-top: 0.5rem;
             font-size: 0.9rem; }
    .stub { padding: 1.5rem; text-align: center; color: var(--kfe-muted); }
  `;

  constructor() {
    super();
    /** @type {"grid" | "practice"} */ this._view = "grid";
    this._done = false;
    this._noContent = false;
    /** @type {Concept[]} */ this._concepts = [];
    /** @type {number | undefined} wall-clock ms this SR session started (Brief 025) */
    this._startedAtMs = undefined;
    this._index = 0;
    /** @type {Record<number, WordProgress>} */ this._progress = {};
    // One-shot guard for item_completed (Brief 171 invariant — fires exactly
    // once). Set true when we emit, OR when hydration restores an already-
    // complete item (Brief 172) so reopening a finished episode never re-fires.
    this._emittedComplete = false;
    /** @type {"manual" | "automatic"} */ this._mode = "manual";
    /** @type {string | null} */ this._stimulusImageUrl = null;
    /** @type {Recorder | null} */ this._recorder = null;
    this._isRecording = false;
    this._level = 0;
    /** @type {string | null} */ this._playbackUrl = null; // recording for the CURRENT word
    this._hasNewRecord = false;                            // iOS isNewReocrd
    /** @type {string | null} */ this._micError = null;
    this._infoOpen = false;
    this._volume = 1.0;
    this._speed = 1.0;
    /** @type {HTMLAudioElement | null} */ this._recordingAudio = null;
    /** @type {ReturnType<typeof setTimeout> | null} */ this._maxRecordTimer = null;
    this._lastAudioConceptId = /** @type {number | null} */ (null);
  }

  async onConnected() {
    // Brief 025: epoch-ms session start for the CRM test-instance row.
    this._startedAtMs = Date.now();
    await services.mmus.loadMmuRegistry();
    await services.items.loadContentItems();
    await services.srConfig.loadSrConfig();

    this._setHeaderTitle();

    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) {
      this._noContent = true;
      this._scheduleRender();
      return;
    }

    const concepts = await resolveConceptsViaServices(itemId);
    if (concepts.length === 0) {
      this._noContent = true;
      this._mode = inferModeForRender(itemId);
      this._scheduleRender();
      return;
    }
    this._mode = inferModeForRender(itemId);
    this._concepts = concepts;
    this._initProgress();
    await this._hydrateProgress(itemId);

    this.addCleanup(() => this._releaseRecorder());
    this.addCleanup(() => this._revokePlaybackUrl());
    this.addCleanup(() => services.audio.stop());
    this.addCleanup(() => {
      if (this._maxRecordTimer) clearTimeout(this._maxRecordTimer);
    });

    this._scheduleRender();
  }

  onDisconnected() {
    // addCleanup callbacks handle teardown; nothing else to do.
  }

  _setHeaderTitle() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(itemId) ? /** @type {any} */ (services.items.getItem?.(itemId)) : null;
    const title = item?.title || item?.name || t("sr.title");
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title },
      bubbles: true,
      composed: true,
    }));
  }

  _initProgress() {
    /** @type {Record<number, WordProgress>} */
    const progress = {};
    for (const c of this._concepts) {
      // SR per-word required plays: the iOS mustPlayCount. The web data layer
      // (Brief 060) carries no per-MMU mustPlayCount, so default to 1 (the iOS
      // "0/1" tile counter). Honour an explicit value if a concept carries one.
      const mustPlay = Number(/** @type {any} */ (c).mustPlay) || 1;
      progress[c.mmuId] = { playSoFar: 0, mustPlay, completed: false };
    }
    this._progress = progress;
  }

  /**
   * Restore per-word progress from the learner store before first paint (Brief
   * 172 — cross-reload persistence). Mirrors iOS loadPlayInfoWithLessonName +
   * getAllPlayContentsWithPInfo, which read the stored srLessonContentsInfo rows
   * for the SR exercise. Per-item scoped (the store key is [itemId, mmuId]); a
   * word with no stored row keeps the `_initProgress` default.
   *
   * @param {number} itemId
   */
  async _hydrateProgress(itemId) {
    /** @type {Record<number, WordProgress>} */
    let stored = {};
    try {
      stored = await services.learner.getSelfRecordingProgressForItem(itemId);
    } catch (err) {
      console.warn("[sr] hydrate progress failed", err);
      return;
    }
    for (const c of this._concepts) {
      const s = stored[c.mmuId];
      if (!s) continue;
      // Keep the in-session mustPlay (authored/default) but restore the learner's
      // accumulated plays + completion.
      const mustPlay = this._progress[c.mmuId]?.mustPlay ?? s.mustPlay ?? 1;
      this._progress[c.mmuId] = {
        playSoFar: s.playSoFar ?? 0,
        mustPlay,
        completed: !!s.completed,
      };
    }
    // If the episode was already finished in a prior session, item_completed has
    // already fired + persisted at item granularity — suppress a re-fire (the
    // grid simply shows every tile ✓).
    if (this._allCompleted()) this._emittedComplete = true;
  }

  // ─── View navigation (grid ⇄ practice) ────────────────────

  /** @param {number} index */
  _openPractice(index) {
    if (index < 0 || index >= this._concepts.length) return;
    this._resetWordState();
    this._index = index;
    this._view = "practice";
    this._scheduleRender();
    this._playTarget(/*force*/ false);
  }

  _backToGrid() {
    this._resetWordState();
    services.audio.stop();
    this._view = "grid";
    this._scheduleRender();
  }

  /** Reset the transient per-word recording state when switching words. */
  _resetWordState() {
    this._revokePlaybackUrl();
    // Abort any in-flight recording but KEEP the mic stream alive — a word
    // switch is still within the same mount, so re-acquiring would re-prompt
    // for permission (Brief 173 Sub-fix 6). Full release happens on disconnect.
    this._cancelRecording();
    this._hasNewRecord = false;
    this._infoOpen = false;
    this._micError = null;
    this._level = 0;
    this._stimulusImageUrl = null;
    if (this._maxRecordTimer) { clearTimeout(this._maxRecordTimer); this._maxRecordTimer = null; }
  }

  // ─── Control-bar intents ──────────────────────────────────

  _handlePrev() {
    if (this._index <= 0) return;
    this._openPractice(this._index - 1);
  }

  _handleNext() {
    // Gated on current-word completion (iOS nextOneBtn.enabled, VC.m:175-181);
    // the control bar disables ⏭ when not allowed, but guard here too.
    if (!this._currentCompleted()) return;
    if (this._index >= this._concepts.length - 1) return;
    this._openPractice(this._index + 1);
  }

  _handlePlayModel() {
    this._playTarget(/*force*/ true);
  }

  async _handleRecord() {
    if (this._isRecording) return;
    if (typeof MediaRecorder === "undefined") {
      this._micError = t("sr.notSupported");
      this._scheduleRender();
      return;
    }
    // New attempt overwrites any prior recording for this word.
    this._revokePlaybackUrl();
    this._hasNewRecord = false;
    this._micError = null;
    services.audio.stop(); // don't let model audio leak into the mic

    if (!this._recorder) this._recorder = new Recorder();
    this._recorder.onLevel = (lvl) => {
      this._level = lvl;
      const fill = this.shadowRoot?.querySelector(".level-fill");
      if (fill instanceof HTMLElement) {
        fill.style.right = (100 - Math.min(100, lvl * 200)) + "%";
      }
    };

    try {
      await this._recorder.start();
    } catch (err) {
      this._isRecording = false;
      this._micError = isPermissionError(err) ? t("sr.micDenied") : t("sr.notSupported");
      // A failed acquisition leaves no usable stream — fully release so the next
      // tap re-acquires cleanly (and re-prompts if the learner grants later).
      this._releaseRecorder();
      this._scheduleRender();
      return;
    }
    this._isRecording = true;
    this._scheduleRender();

    if (this._maxRecordTimer) clearTimeout(this._maxRecordTimer);
    this._maxRecordTimer = setTimeout(() => {
      this._maxRecordTimer = null;
      if (this._isRecording) this._handleStopRecord();
    }, MAX_RECORD_MS);
  }

  async _handleStopRecord() {
    if (!this._recorder || !this._isRecording) return;
    if (this._maxRecordTimer) { clearTimeout(this._maxRecordTimer); this._maxRecordTimer = null; }

    const blob = await this._recorder.stop();
    // Brief 173 Sub-fix 6: KEEP the Recorder (and its live mic stream) so the
    // next record press reuses the same stream instead of re-prompting. The
    // one-slot transient guarantee (Brief 053) is preserved by Recorder.start(),
    // which resets `chunks` each call. Full release happens on disconnect.
    this._isRecording = false;
    this._level = 0;
    if (blob.size > 0) {
      this._playbackUrl = URL.createObjectURL(blob);
      this._hasNewRecord = true; // iOS isNewReocrd — next playback counts a "play so far"
    }
    this._scheduleRender();
  }

  /**
   * Play the learner's own recording. iOS canon
   * (KFESelfRecordingViewModel.m:349-358): the FIRST playback after a fresh
   * capture increments playsSoFarCount and re-checks completion.
   */
  _handlePlayRecording() {
    if (!this._playbackUrl) return;
    // Play at the chosen volume (iOS plays the recording at soundVolume, normal
    // rate — only the MODEL audio honours playSpeed).
    if (this._recordingAudio) { this._recordingAudio.pause(); }
    const audio = new Audio(this._playbackUrl);
    audio.volume = this._volume;
    this._recordingAudio = audio;
    audio.play().catch((err) => console.warn("[sr] recording playback failed", err));

    if (this._hasNewRecord) {
      this._hasNewRecord = false;
      this._recordWordPlay();
    }
  }

  /** Increment the current word's playsSoFarCount; complete it / the item. */
  _recordWordPlay() {
    const mmu = this._currentConcept();
    if (!mmu) return;
    const p = this._progress[mmu.mmuId] ?? { playSoFar: 0, mustPlay: 1, completed: false };
    p.playSoFar += 1;
    if (p.playSoFar >= p.mustPlay) p.completed = true;
    this._progress[mmu.mmuId] = p;

    // Persist the increment (Brief 172). iOS writes per-word on every increment
    // (updateDisplayingItemToLocal); fire-and-forget so the UI never blocks.
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      Promise.resolve(services.learner.setSelfRecordingProgress(itemId, mmu.mmuId, p))
        .catch((err) => console.warn("[sr] persist word progress failed", mmu.mmuId, err));
    }

    if (this._allCompleted() && !this._emittedComplete) {
      this._emittedComplete = true;
      this._done = true;
      this._emitItemCompleted();
    }
    this._scheduleRender();
  }

  /** @param {number} value 0..1 */
  _handleVolumeChange(value) {
    this._volume = value;
    if (this._recordingAudio) this._recordingAudio.volume = value;
    // No re-render: the slider already reflects the value; re-rendering would
    // rebuild the bar and drop the slider's drag focus.
  }

  /** @param {number} speed */
  _handleSpeedChange(speed) {
    this._speed = speed;
    this._refreshControlBar();
  }

  _toggleInfo() {
    this._infoOpen = !this._infoOpen;
    this._scheduleRender();
  }

  // ─── Helpers ──────────────────────────────────────────────

  _currentConcept() {
    return this._concepts[this._index] ?? null;
  }

  _currentProgress() {
    const mmu = this._currentConcept();
    return (mmu && this._progress[mmu.mmuId]) || { playSoFar: 0, mustPlay: 1, completed: false };
  }

  _currentCompleted() {
    return this._currentProgress().completed;
  }

  _allCompleted() {
    return this._concepts.every((c) => this._progress[c.mmuId]?.completed);
  }

  _playTarget(force) {
    const mmu = this._currentConcept();
    if (!mmu) return;
    if (!force && this._lastAudioConceptId === mmu.mmuId) return;
    this._lastAudioConceptId = mmu.mmuId;
    services.audio.play(mmu, { volume: this._volume, rate: this._speed }).catch((err) => {
      console.warn("[sr] target audio play failed for", mmu.mmuId, err);
    });
    if (this._needsStimulusImage(mmu)) this._resolveStimulusImage(mmu);
  }

  /** @param {Concept} mmu */
  _needsStimulusImage(mmu) {
    return mmu.displayType === "audio_image" || mmu.displayType === "audio_image_text";
  }

  /** @param {Concept} mmu */
  async _resolveStimulusImage(mmu) {
    this._stimulusImageUrl = null;
    // SR stimulus images are L2B-style .png line drawings (Brief 062 hint).
    const url = await resolveImage({ ...mmu, imageExt: "png" });
    if (this._currentConcept()?.mmuId !== mmu.mmuId) return; // user advanced
    this._stimulusImageUrl = url ?? null;
    this._scheduleRender();
  }

  _revokePlaybackUrl() {
    if (this._recordingAudio) { try { this._recordingAudio.pause(); } catch { /* no-op */ } this._recordingAudio = null; }
    if (this._playbackUrl) {
      URL.revokeObjectURL(this._playbackUrl);
      this._playbackUrl = null;
    }
  }

  /**
   * Abort an in-flight recording but KEEP the Recorder + its mic stream alive
   * (a word switch within the same mount). Brief 173 Sub-fix 6.
   */
  _cancelRecording() {
    if (this._recorder) {
      try { this._recorder.cancel(); } catch { /* no-op */ }
    }
    this._isRecording = false;
  }

  /**
   * Full recorder teardown — stop the mic tracks + close the AudioContext and
   * drop the instance. Called from disconnectedCallback (and on a failed
   * acquisition). Brief 173 Sub-fixes 1 + 6.
   */
  _releaseRecorder() {
    if (this._recorder) {
      try { this._recorder.release(); } catch { /* no-op */ }
      this._recorder = null;
    }
    this._isRecording = false;
  }

  _emitItemCompleted() {
    const itemId = String(/** @type {any} */ (this).itemId ?? "");
    // Brief 025: queue the CRM `learner_test_instances` metadata row. The
    // RECORDING ITSELF IS NEVER UPLOADED — captured audio stays on the device
    // (Brief 025 §Out of scope, confirmed by iOS canon); only this metadata row
    // syncs. `learner_lesson_items` for the same completion is queued by
    // <kfe-exercise-host>'s markItemCompleted, which the `item_completed`
    // emit below triggers.
    const numericItemId = Number(itemId);
    if (Number.isFinite(numericItemId) && numericItemId > 0) {
      services.learner.recordSession({
        startedAt: this._startedAtMs ?? Date.now(),
        endedAt: Date.now(),
        exerciseType: "self_record",
        itemId: numericItemId,
        summary: { attempts: this._concepts?.length ?? 0, passed: true },
      }).catch((err) => console.warn("[sr] recordSession failed", err));
    }
    this.emit("item_completed", { itemId });
  }

  /** Update the live control bar's state in place (no full re-render). */
  _refreshControlBar() {
    const bar = this.shadowRoot?.querySelector("kfe-recording-control-bar");
    if (bar) /** @type {any} */ (bar).state = this._controlBarState();
  }

  /** @returns {import("../ui/kfe-recording-control-bar.js").ControlBarState} */
  _controlBarState() {
    const p = this._currentProgress();
    return {
      isRecording: this._isRecording,
      hasRecording: !!this._playbackUrl,
      num: this._index + 1,
      total: this._concepts.length,
      playSoFar: p.playSoFar,
      mustPlay: p.mustPlay,
      volume: this._volume,
      speed: this._speed,
      canPrev: this._index > 0,
      canNext: p.completed && this._index < this._concepts.length - 1,
    };
  }

  // Brief 049 completion-screen handlers (KFESRCompletedVC stand-in).
  async _onRedo() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      const ids = this._concepts.map((m) => m.mmuId);
      try {
        await services.learner.clearItemProgress(itemId, ids);
        // Brief 172: also drop the persisted per-word SR rows so the re-walk
        // starts at zero (symmetric with the item-level clear above).
        await services.learner.clearSelfRecordingProgressForItem(itemId, ids);
      } catch (err) {
        console.warn("[sr] clearItemProgress failed", err);
      }
    }
    this._resetWordState();
    services.audio.stop();
    this._done = false;
    this._emittedComplete = false;
    this._index = 0;
    this._view = "grid";
    this._lastAudioConceptId = null;

    // Re-resolve concepts (Automatic mode picks a fresh random sample).
    const fresh = Number.isFinite(itemId) ? await resolveConceptsViaServices(itemId) : [];
    if (fresh.length === 0) {
      this._noContent = true;
      this._concepts = [];
    } else {
      this._noContent = false;
      this._concepts = fresh;
      this._initProgress();
    }
    this._scheduleRender();
  }

  _onReturnToLesson() {
    history.back();
  }

  // ─── Render ───────────────────────────────────────────────

  render() {
    if (this._noContent) {
      return html`<div class="stub">
        <h2>${t("sr.title")}</h2>
        <p>${t("sr.empty")}</p>
      </div>`;
    }
    if (this._concepts.length === 0) return html`<p>${t("loading")}</p>`;
    if (this._done) return this._renderComplete();
    if (this._view === "grid") return this._renderGrid();
    return this._renderPractice();
  }

  _renderGrid() {
    const grid = document.createElement("kfe-self-record-grid");
    /** @type {any} */ (grid).concepts = this._concepts;
    /** @type {any} */ (grid).progress = this._progress;
    /** @type {any} */ (grid).currentMmuId = this._currentConcept()?.mmuId ?? null;
    grid.addEventListener("tile:tap", (e) => {
      const detail = /** @type {CustomEvent<{ index: number }>} */ (e).detail;
      this._openPractice(detail.index);
    });
    return html`${grid}`;
  }

  _renderComplete() {
    // Brief 049: iOS-canonical Self-Recording summary (KFESRCompletedVC stand-in)
    // — body prose above a Yes/No pair (好/不). Yes == redo, No == return.
    const screen = document.createElement("kfe-completion-screen");
    /** @type {any} */ (screen).message = t("sr.complete");
    /** @type {any} */ (screen).prompt = t("completion.selfRecording.prompt");
    /** @type {any} */ (screen).actions = [
      { label: t("completion.selfRecording.yes"), eventName: "kfe:selfRecording:yes", primary: true },
      { label: t("completion.selfRecording.no"), eventName: "kfe:selfRecording:no" },
    ];
    screen.addEventListener("kfe:selfRecording:yes", () => this._onRedo());
    screen.addEventListener("kfe:selfRecording:no", () => this._onReturnToLesson());
    return html`${screen}`;
  }

  _renderPractice() {
    const mmu = this._currentConcept();
    if (!mmu) return html`<p>${t("loading")}</p>`;

    const words = document.createElement("button");
    words.type = "button";
    words.className = "words";
    words.textContent = `‹ ${t("sr.words")}`;
    words.addEventListener("click", () => this._backToGrid());

    const bar = document.createElement("kfe-recording-control-bar");
    /** @type {any} */ (bar).state = this._controlBarState();
    bar.addEventListener("nav:prev", () => this._handlePrev());
    bar.addEventListener("nav:next", () => this._handleNext());
    bar.addEventListener("playback:model", () => this._handlePlayModel());
    bar.addEventListener("playback:recording", () => this._handlePlayRecording());
    bar.addEventListener("record:start", () => this._handleRecord());
    bar.addEventListener("record:stop", () => this._handleStopRecord());
    bar.addEventListener("volume:change", (e) =>
      this._handleVolumeChange(/** @type {CustomEvent<{value:number}>} */ (e).detail.value));
    bar.addEventListener("speed:change", (e) =>
      this._handleSpeedChange(/** @type {CustomEvent<{speed:number}>} */ (e).detail.speed));

    return html`
      <div class="toolbar">${words}</div>
      ${this._renderStimulus(mmu)}
      ${this._renderRecordingArea()}
      ${bar}
      ${this._micError ? html`<div class="error">${this._micError}</div>` : ""}
    `;
  }

  /** @param {Concept} mmu */
  _renderStimulus(mmu) {
    // iOS KFESelfRecordingVC.m setViewsWithData (m:199-243):
    //   Image      → image only (no word)
    //   ImageText  → image + word (contentTextLabel)
    //   Text       → word (contentLabel)
    //   AudioOnly  → "(Audio)" placeholder (contentLabel = "(Audio)")
    const showImage = mmu.displayType === "audio_image" || mmu.displayType === "audio_image_text";
    const showText  = mmu.displayType === "audio_text"  || mmu.displayType === "audio_image_text";
    const audioOnly = mmu.displayType === "audio";

    const wrap = document.createElement("div");
    wrap.className = "stimulus";
    wrap.dataset.displayType = mmu.displayType;

    if (showImage) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "l2b-image-container";
      if (this._stimulusImageUrl) {
        const img = document.createElement("img");
        img.alt = mmu.phrase;
        img.loading = "eager";
        img.src = this._stimulusImageUrl;
        imgWrap.appendChild(img);
      } else {
        // No asset resolved — paint the phrase rather than a broken <img>
        // (Brief 062). resolveImage's re-render swaps in the <img> if it loads.
        imgWrap.classList.add("no-image");
        imgWrap.textContent = mmu.phrase;
      }
      wrap.appendChild(imgWrap);
    }
    if (showText) {
      const span = document.createElement("div");
      span.className = "word";
      span.textContent = mmu.phrase;
      wrap.appendChild(span);
    }
    if (audioOnly) {
      const lbl = document.createElement("div");
      lbl.className = "audio-only-label";
      lbl.textContent = "(Audio)";
      wrap.appendChild(lbl);
    }

    // Info button (ⓘ) — toggles the Chinese translation (iOS infoLabel = textZh,
    // VC.m:504-509). Disabled when the MMU has no zh translation (VC.m:192-196).
    const hasZh = !!mmu.phraseZh;
    const info = document.createElement("button");
    info.type = "button";
    info.className = "info-btn";
    info.textContent = "ⓘ";
    info.setAttribute("aria-label", t("sr.info"));
    info.setAttribute("aria-pressed", String(this._infoOpen && hasZh));
    info.disabled = !hasZh;
    info.addEventListener("click", () => this._toggleInfo());
    wrap.appendChild(info);

    const frag = document.createDocumentFragment();
    frag.appendChild(wrap);
    if (this._infoOpen && hasZh) {
      const zh = document.createElement("p");
      zh.className = "info-label";
      zh.textContent = mmu.phraseZh;
      frag.appendChild(zh);
    }
    return frag;
  }

  _renderRecordingArea() {
    const frag = document.createDocumentFragment();
    if (this._isRecording) {
      const meter = document.createElement("div");
      meter.className = "level";
      const fill = document.createElement("div");
      fill.className = "level-fill";
      fill.style.right = (100 - Math.min(100, this._level * 200)) + "%";
      meter.appendChild(fill);
      frag.appendChild(meter);

      const label = document.createElement("div");
      label.className = "recording-label";
      label.textContent = t("sr.recording");
      frag.appendChild(label);
    }
    return frag;
  }
}

/**
 * Resolve concepts using the live platform services. The engine
 * (src/engines/sr-engine.js) does the work — this adapter threads services in.
 *
 * @param {number} itemId
 * @returns {Promise<Concept[]>}
 */
async function resolveConceptsViaServices(itemId) {
  // Automatic mode needs the item→media→word index (Brief 060b). Lazy-load it
  // here — only when an SR item is actually opened — so the ~835 KB
  // media-words.json never lands on learners who don't reach Self-Recording.
  if (services.srConfig.getSrConfig(itemId)?.mode === "automatic") {
    await services.itemMedia.loadItemMedia();
  }
  return engineResolveConcepts(itemId, {
    getSrConfig: services.srConfig.getSrConfig,
    getMmu: services.mmus.getMmu,
    getMmusForItem: services.mmus.getMmusForItem,
    getCompletedItemIds: services.learner.getCompletedItemIds,
    getWordsForItem: services.itemMedia.getWordsForItem,
    getAllSrMmus: () =>
      services.mmus.getAllSr().map((m) => ({
        ...m,
        wordIds: services.itemMedia.getWordsForMmu(m.mmuId),
      })),
  });
}

/**
 * Infer the mode just for the render path — used to surface the correct
 * empty-state copy. Falls back to "manual" if sr-config.json doesn't know
 * about this item (legacy/non-SR fallthrough).
 *
 * @param {number} itemId
 * @returns {"manual" | "automatic"}
 */
function inferModeForRender(itemId) {
  return services.srConfig.getSrConfig(itemId)?.mode ?? "manual";
}

/** @param {unknown} err */
function isPermissionError(err) {
  if (!err || typeof err !== "object") return false;
  const name = /** @type {{ name?: string }} */ (err).name;
  return name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError";
}
